import { useCallback, useEffect, useState } from "react";
import { Save, RotateCcw, Megaphone } from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";

type AktionContent = {
    enabled: boolean;
    trigger_utm_param: string;
    trigger_utm_value: string;
    banner_text: string;
    banner_cta_label: string;
    hero_chip_text: string;
    section_eyebrow: string;
    section_headline: string;
    section_body: string;
    price_regular_text: string;
    price_advantage_text: string;
    section_cta_label: string;
    slots_visible: boolean;
    slots_remaining: number;
    slots_total: number;
    slots_label_template: string;
    slots_shimmer_intensity: "off" | "subtle" | "medium" | "strong";
};

const DEFAULTS: AktionContent = {
    enabled: false,
    trigger_utm_param: "utm_campaign",
    trigger_utm_value: "referenzaktion",
    banner_text: "Exklusive Referenzaktion · 20 Immobilien · 80 % Preisvorteil für Referenzkunden",
    banner_cta_label: "Mehr erfahren →",
    hero_chip_text: "Exklusive Referenzaktion · 80 % Preisvorteil",
    section_eyebrow: "Exklusive Referenzaktion",
    section_headline: "20 Immobilien. 80 % Preisvorteil.",
    section_body: (
        "Für den Aufbau unseres Referenz-Portfolios begleiten wir 20 ausgewählte Objekte zu Sonderkonditionen. "
        + "Gegenleistung: Wir dürfen Ihre Dokumentation als Referenz nutzen. Unverbindliche Bewerbung — HypeFly wählt aus."
    ),
    price_regular_text: "Regulärer Preis: ab 199 €",
    price_advantage_text: "80 % Preisvorteil – nur für ausgewählte Referenzkunden",
    section_cta_label: "Für Referenzaktion bewerben →",
    slots_visible: true,
    slots_remaining: 20,
    slots_total: 20,
    slots_label_template: "{remaining} von {total} noch frei",
    slots_shimmer_intensity: "medium",
};

const SHIMMER_OPTIONS: { value: AktionContent["slots_shimmer_intensity"]; label: string; desc: string }[] = [
    { value: "off",    label: "Aus",    desc: "Statischer Gold-Chip, kein Puls" },
    { value: "subtle", label: "Dezent", desc: "Leichter Gold-Puls" },
    { value: "medium", label: "Mittel", desc: "Deutlich pulsierendes Gold" },
    { value: "strong", label: "Stark",  desc: "Kräftig leuchtendes Voll-Gold" },
];

export default function AdminAktionPage() {
    const [content, setContent] = useState<AktionContent>(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api<{ items: Record<string, unknown> }>("/api/admin/site-content");
        if (res.ok && res.data) {
            const raw = (res.data.items?.["aktion"] || {}) as Partial<AktionContent>;
            setContent({ ...DEFAULTS, ...raw });
        }
        setLoading(false);
        setDirty(false);
    }, []);
    useEffect(() => { void load(); }, [load]);

    const patch = <K extends keyof AktionContent>(k: K, v: AktionContent[K]) => {
        setContent((prev) => ({ ...prev, [k]: v }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        const res = await api("/api/admin/site-content/aktion", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: content }),
        });
        setSaving(false);
        if (res.ok) {
            setDirty(false);
            toast.success("Referenz-Aktion gespeichert.");
        } else {
            toast.error(res.error || "Speichern fehlgeschlagen.");
        }
    };

    const resetToDefaults = () => {
        if (!window.confirm("Standardwerte wiederherstellen? (Speichern erst danach)")) return;
        setContent({ ...DEFAULTS });
        setDirty(true);
    };

    if (loading) return <div className="text-hf-muted text-sm">Wird geladen…</div>;

    return (
        <div data-testid="admin-aktion">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="hf-eyebrow">Kampagnen-Steuerung</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">
                        <Megaphone size={22} className="inline-block mr-2 opacity-70" />
                        Referenz-Aktion
                    </h1>
                    <p className="text-hf-muted text-sm mt-2 max-w-2xl">
                        Wenn aktiviert, ist die Aktion für <strong>alle Besucher</strong> sichtbar:
                        Streifen oben (auf allen öffentlichen Seiten), Hero-Chip und Aktions-Sektion
                        auf der Startseite. Steuerung erfolgt ausschließlich über den Aktion-Schalter unten.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={resetToDefaults}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider rounded-sm"
                        style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))", color: "hsl(var(--hf-muted))" }}
                        data-testid="admin-aktion-reset"
                    ><RotateCcw size={12} /> Standard</button>
                    <button
                        type="button"
                        onClick={() => void save()}
                        disabled={!dirty || saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wider rounded-sm"
                        style={{
                            background: dirty ? "hsl(var(--hf-ink))" : "hsl(var(--hf-surface))",
                            color: dirty ? "hsl(var(--hf-bg))" : "hsl(var(--hf-muted))",
                            cursor: saving ? "wait" : (dirty ? "pointer" : "not-allowed"),
                            opacity: saving ? 0.7 : 1,
                        }}
                        data-testid="admin-aktion-save"
                    ><Save size={12} /> {saving ? "Speichert…" : "Speichern"}</button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Editor */}
                <div
                    className="p-6 rounded-sm space-y-6"
                    style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                >
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={content.enabled}
                            onChange={(e) => patch("enabled", e.target.checked)}
                            className="mt-1"
                            data-testid="admin-aktion-enabled"
                        />
                        <div>
                            <div className="text-sm font-medium">Aktion aktiv</div>
                            <div className="text-xs text-hf-muted">
                                Wenn <strong>an</strong>: Streifen + Hero-Chip + Aktions-Sektion sind für alle
                                Besucher sichtbar. Wenn <strong>aus</strong>: öffentliche Seiten unverändert.
                            </div>
                        </div>
                    </label>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-hf-muted mb-2">Aktions-Streifen (oben, sticky)</div>
                        <div className="space-y-3">
                            <TextField label="Streifen-Text" value={content.banner_text}
                                onChange={(v) => patch("banner_text", v)} testId="admin-aktion-banner-text" />
                            <TextField label="Streifen-CTA-Label" value={content.banner_cta_label}
                                onChange={(v) => patch("banner_cta_label", v)} testId="admin-aktion-banner-cta" />
                        </div>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-hf-muted mb-2">Hero-Chip</div>
                        <TextField label="Chip-Text (dezent, unter Hero-Headline)" value={content.hero_chip_text}
                            onChange={(v) => patch("hero_chip_text", v)} testId="admin-aktion-chip-text" />
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-hf-muted mb-2">Aktions-Sektion</div>
                        <div className="space-y-3">
                            <TextField label="Eyebrow" value={content.section_eyebrow}
                                onChange={(v) => patch("section_eyebrow", v)} testId="admin-aktion-section-eyebrow" />
                            <TextField label="Headline" value={content.section_headline}
                                onChange={(v) => patch("section_headline", v)} testId="admin-aktion-section-headline" />
                            <TextAreaField label="Body-Text" value={content.section_body} rows={4}
                                onChange={(v) => patch("section_body", v)} testId="admin-aktion-section-body" />
                            <div className="grid grid-cols-2 gap-3">
                                <TextField label="Regulärer Preis" value={content.price_regular_text}
                                    onChange={(v) => patch("price_regular_text", v)} testId="admin-aktion-price-regular" />
                                <TextField label="Preisvorteil" value={content.price_advantage_text}
                                    onChange={(v) => patch("price_advantage_text", v)} testId="admin-aktion-price-advantage" />
                            </div>
                            <TextField label="Primary-CTA-Label" value={content.section_cta_label}
                                onChange={(v) => patch("section_cta_label", v)} testId="admin-aktion-section-cta" />
                        </div>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-hf-muted mb-2">
                            Verfügbarkeits-Anzeige
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer mb-3">
                            <input
                                type="checkbox"
                                checked={content.slots_visible}
                                onChange={(e) => patch("slots_visible", e.target.checked)}
                                className="mt-1"
                                data-testid="admin-aktion-slots-visible"
                            />
                            <div>
                                <div className="text-sm font-medium">Verfügbarkeit einblenden</div>
                                <div className="text-xs text-hf-muted">
                                    Kleines Chip in der Aktions-Sektion („X von Y noch frei“).
                                    Manuell gepflegt — es wird nichts aus der DB gezählt.
                                </div>
                            </div>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <NumberField label="Noch frei" value={content.slots_remaining}
                                onChange={(v) => patch("slots_remaining", v)} testId="admin-aktion-slots-remaining" />
                            <NumberField label="Gesamt" value={content.slots_total}
                                onChange={(v) => patch("slots_total", v)} testId="admin-aktion-slots-total" />
                        </div>
                        <div className="mt-3">
                            <TextField label="Text-Vorlage" value={content.slots_label_template}
                                onChange={(v) => patch("slots_label_template", v)} testId="admin-aktion-slots-template" />
                            <div className="text-[11px] text-hf-muted mt-1">
                                Platzhalter: <code className="font-mono">{"{remaining}"}</code> ·{" "}
                                <code className="font-mono">{"{total}"}</code>.
                                Bei „Noch frei = 0“ wird automatisch „Aktion vollständig belegt“ angezeigt.
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="text-xs uppercase tracking-wider text-hf-muted mb-2">
                                Leuchtstärke Shimmer-Effekt
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-aktion-shimmer-group">
                                {SHIMMER_OPTIONS.map((opt) => {
                                    const active = content.slots_shimmer_intensity === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => patch("slots_shimmer_intensity", opt.value)}
                                            className="text-left px-3 py-2 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                                            style={{
                                                background: active ? "#FDF7EC" : "hsl(var(--hf-surface))",
                                                border: `1px solid ${active ? "#C48B34" : "hsl(var(--hf-line))"}`,
                                                color: "hsl(var(--hf-ink))",
                                            }}
                                            data-testid={`admin-aktion-shimmer-${opt.value}`}
                                            aria-pressed={active}
                                        >
                                            <div className="text-sm font-medium">{opt.label}</div>
                                            <div className="text-[10px] text-hf-muted mt-0.5 leading-tight">{opt.desc}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Preview */}
                <div
                    className="p-6 rounded-sm min-h-[500px] flex flex-col gap-4"
                    style={{ background: "hsl(var(--hf-surface))", border: "1px dashed hsl(var(--hf-line))" }}
                    data-testid="admin-aktion-preview"
                >
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Live-Vorschau (Aktions-Zustand aktiviert)</div>

                    {/* Banner Preview */}
                    <div
                        style={{
                            background: "#FFFDF9",
                            border: "1px solid #D8CDBF",
                            color: "#1D1B18",
                            padding: "10px 14px",
                        }}
                        className="rounded-sm text-xs md:text-sm flex items-center gap-3"
                    >
                        <span
                            className="uppercase text-[9px] tracking-[0.16em] px-2 py-0.5 rounded-sm"
                            style={{ background: "#A78150", color: "#FFFDF9" }}
                        >Aktion</span>
                        <span className="flex-1 truncate">{content.banner_text}</span>
                        <span
                            className="text-xs px-3 py-1 rounded-sm"
                            style={{ background: "#A78150", color: "#FFFDF9" }}
                        >{content.banner_cta_label}</span>
                    </div>

                    {/* Hero-Chip Preview */}
                    <div>
                        <div className="text-xs text-hf-muted mb-1">Hero-Chip:</div>
                        <span
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full"
                            style={{
                                background: "hsl(var(--hf-gold-soft) / 0.6)",
                                border: "1px solid #A78150",
                                color: "#1D1B18",
                                fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600,
                            }}
                        >
                            <span aria-hidden style={{ width: 6, height: 6, background: "#A78150", borderRadius: 999 }} />
                            {content.hero_chip_text}
                        </span>
                    </div>

                    {/* Section Preview */}
                    <div
                        className="rounded-sm p-5 mt-2"
                        style={{ background: "#FFFDF9", border: "1px solid #D8CDBF" }}
                    >
                        <div className="uppercase text-[10px] tracking-[0.24em] flex items-center gap-3 flex-wrap" style={{ color: "#A78150", fontWeight: 600 }}>
                            <span>{content.section_eyebrow}</span>
                            {content.slots_visible && <PreviewSlotsBadge remaining={content.slots_remaining} total={content.slots_total} template={content.slots_label_template} shimmer={content.slots_shimmer_intensity} />}
                        </div>
                        <h3 className="font-display text-xl md:text-2xl mt-2" style={{ color: "hsl(var(--hf-ink))" }}>
                            {content.section_headline}
                        </h3>
                        <p className="mt-3 text-sm" style={{ color: "hsl(var(--hf-muted))" }}>
                            {content.section_body}
                        </p>
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="text-xs p-2 rounded-sm" style={{ background: "hsl(var(--hf-surface))" }}>
                                {content.price_regular_text}
                            </div>
                            <div
                                className="text-xs p-2 rounded-sm"
                                style={{ background: "hsl(var(--hf-gold-soft) / 0.5)", border: "1px solid #A78150" }}
                            >
                                {content.price_advantage_text}
                            </div>
                        </div>
                        <div
                            className="inline-block mt-4 px-4 py-2 text-xs rounded-sm"
                            style={{ background: "#A78150", color: "#FFFDF9" }}
                        >
                            {content.section_cta_label}
                        </div>
                    </div>

                    {!content.enabled && (
                        <div className="text-xs px-3 py-2 rounded-sm mt-2"
                            style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}>
                            Aktion aktuell ausgeschaltet — auf der öffentlichen Seite passiert nichts.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TextField({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId: string }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-hf-muted">{label}</span>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-sm"
                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                data-testid={testId}
            />
        </label>
    );
}

function TextAreaField({ label, value, onChange, testId, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; testId: string; rows?: number }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-hf-muted">{label}</span>
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                className="mt-1 w-full px-3 py-2 text-sm rounded-sm resize-y"
                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                data-testid={testId}
            />
        </label>
    );
}

function NumberField({ label, value, onChange, testId }: { label: string; value: number; onChange: (v: number) => void; testId: string }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-hf-muted">{label}</span>
            <input
                type="number"
                min={0}
                step={1}
                value={Number.isFinite(value) ? value : 0}
                onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    onChange(Number.isFinite(n) ? Math.max(0, n) : 0);
                }}
                className="mt-1 w-full px-3 py-2 text-sm rounded-sm font-mono"
                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                data-testid={testId}
            />
        </label>
    );
}

function PreviewSlotsBadge({ remaining, total, template, shimmer = "medium" }: { remaining: number; total: number; template: string; shimmer?: AktionContent["slots_shimmer_intensity"] }) {
    const r = Math.max(0, Math.floor(Number(remaining) || 0));
    const t = Math.max(0, Math.floor(Number(total) || 0));
    const soldOut = r <= 0;
    const label = soldOut
        ? "Aktion vollständig belegt"
        : (template || "{remaining} von {total} noch frei")
            .replace(/\{remaining\}/g, String(r))
            .replace(/\{total\}/g, String(t));
    const pulseActive = !soldOut && shimmer !== "off";
    const pulseClass = pulseActive ? ` hf-slots-pulse hf-slots-pulse-${shimmer}` : "";
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] tracking-[0.14em] normal-case px-2 py-0.5 rounded-sm${pulseClass}`}
            style={
                soldOut
                    ? {
                        background: "hsl(var(--hf-surface))",
                        border: "1px solid hsl(var(--hf-line))",
                        color: "hsl(var(--hf-muted))",
                        fontWeight: 500,
                    }
                    : pulseActive
                        ? { fontWeight: 600 }
                        : {
                            background: "#FDF7EC",
                            border: "1px solid #E4CFA4",
                            color: "#8A6534",
                            fontWeight: 500,
                        }
            }
            data-testid="admin-aktion-preview-slots"
        >
            <span aria-hidden className="inline-block rounded-full"
                style={{
                    width: 6, height: 6,
                    background: soldOut
                        ? "hsl(var(--hf-muted))"
                        : pulseActive
                            ? "#FFF3D6"
                            : "#C48B34",
                    opacity: soldOut ? 0.6 : 1,
                    boxShadow: pulseActive ? "0 0 4px rgba(255, 220, 140, 0.9)" : undefined,
                }} />
            {label}
        </span>
    );
}
